import { useContext, createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import createApiInstance from "../utilities/apiAuth";
import isTokenExpired from '../utilities/checkTokenExpiration';
import { toast } from 'react-toastify';
import { useSelectedFranchisee } from './SelectedFranchiseeContext';

const ClientContext = createContext();

export function ClientProvider({ children }) {
  const navigate = useNavigate();
  const { selectedFranchisee } = useSelectedFranchisee();
  
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);

  const api = createApiInstance(navigate);

  const fetchClients = async () => {
    if (isTokenExpired()) {
      console.log('Token expired - skipping client fetch');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      if (token) {
        const res = await api.get('/clients/list');
        setClients(res.data);
      } else {
        console.log('No User Logged In');
        setClients([]);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to fetch clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore selectedClient from localStorage on mount
  useEffect(() => {
    const storedClient = localStorage.getItem('selectedClient');
    if (storedClient) {
      try {
        setSelectedClient(JSON.parse(storedClient));
      } catch (e) {
        // If parsing fails, clear the invalid value
        localStorage.removeItem('selectedClient');
      }
    }
  }, []);

  // Automatically update selectedClient in context when selectedFranchisee changes and has a valid assoc_client_type.
  useEffect(() => {
    if (
      selectedFranchisee &&
      selectedFranchisee.assoc_client_type &&
      Array.isArray(clients)
    ) {
      const matchedClient = clients.find(
        (client) => client.id === selectedFranchisee.assoc_client_type
      );
      if (matchedClient) {
        setSelectedClient(matchedClient);
        localStorage.setItem('selectedClient', JSON.stringify(matchedClient));
      }
    }
  }, [selectedFranchisee, clients]);

  const refetchClients = async () => {
    await fetchClients();
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    localStorage.setItem('selectedClient', JSON.stringify(client));
  };

  return (
    <ClientContext.Provider
      value={{
        clients,
        selectedClient,
        setSelectedClient,
        handleClientSelect,
        refetchClients,
        loading
      }}
    >
      {children}
    </ClientContext.Provider>
  );
}

export function useClient() {
  return useContext(ClientContext);
}
